import React, { useEffect, useMemo } from 'react';

import { orderBy } from 'lodash';
import { Controller, useFormContext } from 'react-hook-form';
import { WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useUserHook from 'hooks/useUserHook';

import { useMutationsSetKeyActivitiesTags } from 'api/mutations/setKeyActivities.mutation';
import { useQueryGetKeyActivitiesTags } from 'api/queries/getKeyActivitiesTags.query';
import { useQueryGetStoredTags } from 'api/queries/getStoredTags.query';

import { List } from 'components/common/ListWrapper/list';
import { InputOption } from 'components/common/MyCustomSelect/components/InputOption/InputOption';
import { ValueContainer } from 'components/common/MyCustomSelect/components/ValueContainer/ValueContainer';

import { Select } from 'components/DesignSystem/Select';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';
import { FormProps } from 'components/widgets/KeyActivities/KeyActivities.provider';
import classes from 'components/widgets/KeyActivities/components/filter.module.scss';
import {
    moreFilterOptions,
    nextDateOptions,
    statusOptions,
} from 'components/widgets/KeyActivities/components/filter.utils';

export const Filter = ({ contractId, projectId, widgetViewMode }) => {
    const { setValue, control } = useFormContext<FormProps>();
    const { handleSnackBar } = useCustomSnackBar();
    const { mutate: mutateSetTags } = useMutationsSetKeyActivitiesTags();
    const { ability, customerId } = useUserHook();
    const { data: tagsList } = useQueryGetStoredTags({
        enabled: true,
        customerId,
        projectId: projectId,
        contractId: contractId,
    });

    const { data: tagsFromBe, refetch } = useQueryGetKeyActivitiesTags({
        contractId: contractId,
    });

    const handleSaveTags = (value: readonly ISelectOption<number>[]) => {
        if (WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)) {
            //Dont mutate if in report edit or new mode
            setValue('tags', value, { shouldDirty: true });
        } else {
            mutateSetTags(
                {
                    contractId: contractId,
                    tagIdList: value.map((item) => item.value),
                },
                {
                    onSuccess: () => {
                        setValue('tags', value, { shouldDirty: true });
                        refetch().then(() => {
                            handleSnackBar(
                                'Note Key Activities Tag selection affects all viewers of this page',
                                'success',
                            );
                        });
                    },
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };

    const tagsOptions = useMemo<ISelectOption<number>[]>(
        () =>
            tagsList
                ? orderBy(
                      tagsList.map((item) => ({ label: item.name, value: item.tagId })),
                      [(item) => item.label.toLowerCase()],
                      ['asc'],
                  )
                : [],
        [tagsList],
    );

    useEffect(() => {
        // Sync selected tags with backend values only if tags exist and widget is not in report edit mode
        if (tagsOptions.length > 0 && tagsFromBe && !WidgetViewModeUtils.isReportEditWidget(widgetViewMode)) {
            const tags = tagsOptions.filter((item) => tagsFromBe?.map((item) => item.tagId).includes(item.value));
            setValue('tags', tags, { shouldDirty: true });
        }
    }, [tagsOptions, tagsFromBe]);

    return (
        <div className={classes.filter}>
            <div className={classes.item}>
                <div className={classes.title}>Tags</div>
                <Controller
                    render={({ field }) => (
                        <Select<ISelectOption<number>, true>
                            {...field}
                            options={tagsOptions}
                            onChange={handleSaveTags}
                            isMulti={true}
                            id={'key-activities-tags'}
                            placeholder={'Select a tag which identifies the program’s key activities...'}
                            isDisabled={!ability.can('view', 'KeyAcivitiesSelectTag')}
                        />
                    )}
                    name={'tags'}
                    control={control}
                />
            </div>
            <div className={classes.item}>
                <div className={classes.title}>Name</div>
                <div className={classes.filterTextSearch}>
                    <Controller
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                className={classes.textInput}
                                placeholder={'Search Activities by Name'}
                                maxLength={200}
                                onClear={() => {
                                    setValue('name', '');
                                }}
                            />
                        )}
                        defaultValue={''}
                        name={'name'}
                        control={control}
                    />
                </div>
            </div>
            <div className={classes.item}>
                <div className={classes.title}>Status</div>
                <Controller
                    render={({ field }) => (
                        <Select<ISelectOption<string>, true>
                            {...field}
                            options={statusOptions}
                            isClearable={true}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            isMulti={true}
                            id={'key-activities-status'}
                            placeholder={'Select Status...'}
                            components={{
                                Option: InputOption,
                                ValueContainer: ValueContainer,
                            }}
                        />
                    )}
                    name={'status'}
                    control={control}
                />
            </div>
            <div className={classes.item}>
                <div className={classes.title}>More Filters</div>
                <Controller
                    render={({ field }) => (
                        <Select<ISelectOption<string>, true>
                            {...field}
                            options={moreFilterOptions}
                            isClearable={true}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            isMulti={true}
                            id={'key-activities-more-filters'}
                            placeholder={'Select More Filters...'}
                            components={{
                                Option: InputOption,
                                ValueContainer: ValueContainer,
                            }}
                        />
                    )}
                    name={'moreFilter'}
                    control={control}
                />
            </div>
            <div className={classes.item}>
                <div className={classes.title}>Next</div>
                <div className={classes.filtersWrapper}>
                    <Controller
                        render={({ field }) => <List<string> {...field} options={nextDateOptions} />}
                        name={'nextDate'}
                        control={control}
                    />
                </div>
            </div>
        </div>
    );
};
